import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRESET_SCENARIOS } from "@/lib/defaults";
import { Sprout, FileDown, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR } from "@/lib/format";

export const Header = () => {
  const { activeScenarioName, loadPreset, summary } = useSimulator();

  const handleExportPdf = () => {
    toast.info("Preparazione report PDF...", { duration: 1200 });
    setTimeout(() => window.print(), 400);
  };

  return (
    <header
      data-testid="h2-farm-header"
      className="no-print sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/80"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-500/40 blur-lg rounded-full" />
            <div className="relative p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-slate-950">
              <Sprout className="h-5 w-5" strokeWidth={2.5} />
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display font-extrabold text-lg text-slate-50 tracking-tight">
                H2 Farm
              </span>
              <span className="hidden sm:inline text-[10px] font-mono-tab uppercase tracking-widest text-emerald-400 border border-emerald-500/40 rounded-full px-2 py-0.5 bg-emerald-500/10">
                Simulatore
              </span>
            </div>
            <div className="text-[11px] text-slate-500 truncate hidden md:block">
              Aeroponica · Fotobioreattori · Investor Dashboard
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-6 px-4">
          <TickerItem label="Ricavo" value={fmtEUR(summary.grossRevenue, { compact: true })} color="text-emerald-400" />
          <TickerItem label="EBITDA" value={fmtEUR(summary.operatingMargin, { compact: true })} color="text-sky-400" />
          <TickerItem label="OPEX" value={fmtEUR(summary.totalOpexAnnual, { compact: true })} color="text-rose-400" />
        </div>

        <div className="flex items-center gap-2">
          <Select
            value=""
            onValueChange={(v) => {
              loadPreset(v);
              toast.success(`Preset "${PRESET_SCENARIOS[v].name}" caricato`);
            }}
          >
            <SelectTrigger
              data-testid="scenario-select-dropdown"
              className="w-[180px] bg-slate-950 border-slate-800 text-slate-200 text-sm h-9"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                <SelectValue placeholder={activeScenarioName} />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
              {Object.entries(PRESET_SCENARIOS).map(([key, p]) => (
                <SelectItem key={key} value={key} data-testid={`scenario-option-${key}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            data-testid="export-pdf-button"
            onClick={handleExportPdf}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold h-9"
          >
            <FileDown className="h-4 w-4 mr-1.5" />
            Export PDF
          </Button>
        </div>
      </div>
    </header>
  );
};

const TickerItem = ({ label, value, color }) => (
  <div className="flex flex-col items-end leading-tight">
    <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">
      {label}
    </span>
    <span className={`text-sm font-mono-tab font-semibold ${color}`}>{value}</span>
  </div>
);
